import {
  BadgeDollarSign,
  Landmark,
  Layers3,
  ShieldCheck,
  User,
  Wallet2,
} from "lucide-react";

import { useAppSelector } from "@/store/hooks";

import type { Wallet } from "@/interfaces/wallet";
import { formatCurrency } from "@/utils/formatCurrency";
import { formatEvmHumanBalance } from "@/utils";

interface Props {
  wallet?: Wallet;
}

const WalletSummaryCard = ({ wallet }: Props) => {
  const { theme } = useAppSelector((state) => state.ui);

  if (!wallet) {
    return null;
  }

  return (
    <div
      className={`rounded-2xl border p-5 shadow-sm ${
        theme === "dark"
          ? "bg-gradient-to-br from-card to-muted border-border"
          : "bg-gradient-to-br from-white to-slate-50 border-slate-200"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex gap-4">
          <div
            className={`h-14 w-14 rounded-2xl flex items-center justify-center ${
              wallet.isSystem
                ? "bg-amber-500/15 text-amber-500"
                : "bg-primary/15 text-primary"
            }`}
          >
            {wallet.isSystem ? <Landmark size={28} /> : <User size={28} />}
          </div>

          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-xl font-bold">
                {wallet.isSystem ? `${wallet.systemType} Wallet` : wallet.label}
              </h3>

              <span
                className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  wallet.isFrozen
                    ? "bg-red-500/15 text-red-500"
                    : "bg-emerald-500/15 text-emerald-500"
                }`}
              >
                {wallet.isFrozen ? "Frozen" : "Active"}
              </span>
            </div>

            {wallet.description && (
              <p className="text-sm text-muted-foreground mt-1">
                {wallet.description}
              </p>
            )}
          </div>
        </div>

        <Wallet2 className="text-muted-foreground" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-6">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <BadgeDollarSign size={14} />
              Balance
            </div>

            <div className="text-xl font-bold text-emerald-500">
              {wallet.totalBalanceCurrency}{" "}
              {formatEvmHumanBalance(wallet.totalBalance, false)}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Layers3 size={14} />
              Assets
            </div>

            <div className="text-xl font-bold">
              {wallet.assets?.length ?? 0}
            </div>
          </div>
        </div>

        {wallet.isSystem && (
          <div>
            <div className="text-xs text-muted-foreground">Positive Assets</div>

            <div className="text-xl font-bold text-amber-500">
              {wallet.positiveBalanceAssets}
            </div>
          </div>
        )}
      </div>

      <div
        className={`mt-6 pt-4 border-t flex justify-between text-xs ${
          theme === "dark"
            ? "border-border text-muted-foreground"
            : "border-slate-200 text-slate-500"
        }`}
      >
        <span>
          Wallet ID • {wallet.id.slice(0, 8)}...
          {wallet.id.slice(-4)}
        </span>

        <span>
          {wallet.type === "SYSTEM" ? "System Wallet" : "User Wallet"}
        </span>
      </div>
    </div>
  );
};

export default WalletSummaryCard;
